import { useFilesStore } from "@/store/filesStore";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useKeyboardState } from "@/composables/common/useKeyboardState";

/**
 * 文件选择状态管理模块
 * 负责处理文件的选择、多选等交互逻辑
 */

// 记录上次选中的文件索引，用于 Shift 多选
const lastSelectedIndex = ref(-1);

// 监听 Shift 键的按下状态，用于多选功能
const {
  isKeyPressed: isShiftPressed,
  setupKeyboardEvents,
  cleanupKeyboardEvents,
} = useKeyboardState("Shift");

export const useFilesSelection = () => {
  // 获取文件存储实例
  const filesStore = useFilesStore();
  const {
    files,
    selectedFiles,
    selectedCount,
    isPartiallySelected,
    isAllSelected,
    isSingleSelected,
  } = storeToRefs(filesStore);

  /**
   * 切换文件选中状态
   * 支持单选和 Shift 多选两种模式
   * @param {string} objectId - 文件唯一标识
   */
  const toggleFileChecked = (objectId) => {
    const currentIndex = files.value.findIndex((f) => f.object_id === objectId);
    if (currentIndex === -1) return;

    // 判断是否是 Shift 多选模式
    const isShiftMultiSelect =
      isShiftPressed.value && lastSelectedIndex.value !== -1;

    if (isShiftMultiSelect) {
      handleShiftMultiSelect(currentIndex);
    } else {
      handleSingleSelect(currentIndex);
    }

    // 更新最后选中的索引
    lastSelectedIndex.value = currentIndex;
  };

  /**
   * 处理 Shift 多选
   * 选中当前索引与上次选中索引之间的所有文件
   * @param {number} currentIndex - 当前点击的文件索引
   */
  const handleShiftMultiSelect = (currentIndex) => {
    const [start, end] = [
      Math.min(lastSelectedIndex.value, currentIndex),
      Math.max(lastSelectedIndex.value, currentIndex),
    ];

    // 获取目标选中状态（与当前点击项相反）
    const targetState = !files.value[currentIndex].isChecked;
    // 将范围内的所有文件设置为相同的选中状态
    files.value.slice(start, end + 1).forEach((file) => {
      file.isChecked = targetState;
    });
  };

  /**
   * 处理单选
   * 切换单个文件的选中状态
   * @param {number} currentIndex - 当前点击的文件索引
   */
  const handleSingleSelect = (currentIndex) => {
    files.value[currentIndex].isChecked = !files.value[currentIndex].isChecked;
  };

  /**
   * 切换全选/取消全选
   * @param {boolean} checked - 是否选中
   */
  const toggleAllFiles = (checked) => {
    files.value.forEach((file) => (file.isChecked = checked));
  };

  // 返回状态和操作方法
  return {
    // 状态相关
    state: {
      files,
      selectedFiles,
      selectedCount,
      isPartiallySelected,
      isAllSelected,
      isSingleSelected,
    },
    // 操作方法
    action: {
      toggleFileChecked,
      toggleAllFiles,
      setupKeyboardEvents,
      cleanupKeyboardEvents,
    },
  };
};
